import { clsx } from 'clsx'
import { useEffect, useLayoutEffect, useRef, useSyncExternalStore, type ReactNode } from 'react'
import { t } from '@/lib/i18n'
import { Icon } from './glyphs'
import { motionOn } from './motion'
import { EASE_IN, springEasing, SPRING_MS } from './springs'

/**
 * Toasts (APP-DESIGN-DIRECTION 2.6): bottom centre on desktop, above the phone tab bar on a phone; navy-900, white text,
 * a pill for one line. 4 s, hover pauses, at most three stacked, `role="status"`. An action only where the behaviour
 * already supports it. Rewards that fire after the page's one reward stage arrive here too (3.10).
 */
export interface ToastInput {
  title: string
  body?: string
  /** A leading visual (a badge glyph that pops). */
  icon?: ReactNode
  action?: { label: string; onClick: () => void }
  /** Milliseconds on screen. Default 4000. */
  duration?: number
}
interface ToastItem extends ToastInput { id: number; leaving?: boolean }

let items: ToastItem[] = []
let counter = 0
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((listener) => listener())

export function toast(input: ToastInput): number {
  counter += 1
  items = [...items, { ...input, id: counter }].slice(-3)
  emit()
  return counter
}

export function dismissToast(id: number) {
  if (!items.some((item) => item.id === id)) return
  items = items.map((item) => (item.id === id ? { ...item, leaving: true } : item))
  emit()
  window.setTimeout(() => {
    items = items.filter((item) => item.id !== id)
    emit()
  }, motionOn() ? 180 : 0)
}

function useToasts() {
  return useSyncExternalStore(
    (listener) => { listeners.add(listener); return () => listeners.delete(listener) },
    () => items,
    () => items,
  )
}

function ToastView({ item }: { item: ToastItem }) {
  const ref = useRef<HTMLDivElement>(null)
  const timer = useRef<number>(0)
  const start = () => { timer.current = window.setTimeout(() => dismissToast(item.id), item.duration ?? 4000) }
  useEffect(() => {
    start()
    return () => window.clearTimeout(timer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useLayoutEffect(() => {
    const element = ref.current
    if (!element || !motionOn() || typeof element.animate !== 'function') return
    if (item.leaving) element.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(8px) scale(.98)' }], { duration: 160, easing: EASE_IN, fill: 'forwards' })
    else element.animate([{ opacity: 0, transform: 'translateY(16px) scale(.96)' }, { opacity: 1, transform: 'none' }], { duration: SPRING_MS.snappy, easing: springEasing('snappy') })
  }, [item.leaving])
  return (
    <div
      ref={ref}
      className={clsx('r5-toast', item.body && 'r5-toast--two')}
      onMouseEnter={() => window.clearTimeout(timer.current)}
      onMouseLeave={start}
    >
      {item.icon && <span className="r5-toast__icon r5-pop" data-play="true" aria-hidden="true">{item.icon}</span>}
      <div className="r5-toast__text">
        <p className="r5-toast__title">{item.title}</p>
        {item.body && <p className="r5-toast__body">{item.body}</p>}
      </div>
      {item.action && <button type="button" className="r5-toast__action" onClick={() => { item.action!.onClick(); dismissToast(item.id) }}>{item.action.label}</button>}
      <button type="button" className="r5-toast__close" aria-label={t('Close')} onClick={() => dismissToast(item.id)}><Icon name="close" size={16} /></button>
    </div>
  )
}

/** Mounted once per shell. */
export function Toaster() {
  const list = useToasts()
  return (
    <div className="r5-toaster" role="status" aria-live="polite">
      {list.map((item) => <ToastView key={item.id} item={item} />)}
    </div>
  )
}
